<template>
  <Teleport v-if="mounted" to="#header-breadcrumbs">
    <CBreadcrumb v-bind="{ routes }" />
  </Teleport>
  <CCard class="p-4 md:p-6">
    <div
      class="flex items-start justify-between flex-col md:flex-row w-full gap-3"
    >
      <div class="flex-y-center gap-1 md:gap-3">
        <div
          class="w-[48px] h-[48px] md:w-[72px] md:h-[72px] flex items-center justify-center rounded-full border-gray-800 border-2 relative overflow-hidden"
        >
          <div
            v-if="coinSingle?.base_currency?.image_svg"
            v-html="coinSingle?.base_currency?.image_svg"
            class="coin-image !w-full !h-full object-cover"
          />
          <img
            v-else
            src="/images/default-avatar.png"
            class="w-[46px] h-[46px] object-cover"
            alt="coin"
          />
        </div>
        <div class="flex flex-col justify-center">
          <div class="flex items-center h-auto divide-x space-x-2">
            <p
              class="text-xl md:text-2xl leading-130 font-semibold text-dark-100"
            >
              {{ coinSingle?.base_currency?.title }}
            </p>
            <p
              class="text-lg md:text-xl leading-130 font-medium text-dark-100 pl-2"
            >
              {{ coinSingle?.pair }}
            </p>
          </div>
        </div>
      </div>
      <RouterLink
        class="w-full md:w-auto"
        :to="`/bots/${route.params.botId}/coin/${route.params.coinId}/orders-by-pair`"
      >
        <CButton
          :text="$t('order_history')"
          class="min-w-[165px] max-w-full md:max-w-[165px] !h-10 md:!h-12"
        />
      </RouterLink>
    </div>
    <div class="w-full relative mt-4">
      <Transition name="fade" mode="out-in">
        <div :key="singleLoading">
          <template v-if="singleLoading">
            <CCoinStatisticsShimmer />
          </template>
          <template v-else>
            <gecko-coin-price-chart-widget
              :locale="locale"
              outlined="true"
              :key="coinSingle"
              :width="!trigger ? '1148' : '998'"
              :coin-id="coinID"
              initial-currency="usd"
            />
          </template>
        </div>
      </Transition>
    </div>
    <div
      class="bg-gray-150 border border-gray-250 p-4 md:p-6 rounded-xl grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 mt-5"
    >
      <CProfileDetail
        v-for="(detail, index) in coinActiveDetail"
        :key="index"
        :title="detail?.value"
        :loading="loading"
        :description="detail?.title"
      >
        <template v-if="detail?.type">
          <CCoinProfit
            class="min-w-[170px]"
            :abs="detail?.value?.[0]"
            :pct="detail?.value?.[1]"
          />
        </template>
      </CProfileDetail>
    </div>
  </CCard>
  <CCard class="p-5 mt-5 overflow-x-auto no-scrollbar md-lg:overflow-x-visible">
    <CTableWrapper
      :current-page="1"
      :items-per-page="15"
      :limit="15"
      :title="$t('orders')"
      :data="tableData"
      :head="coinSingle?.is_open ? activeHeadCoins : noactiveHeadCoins"
      th-class="!text-left first:!w-2"
      :loading="loading"
      no-search
      :body-tr-class="'transition-all hover:bg-gray-250/60'"
    >
      <template #_index="{ row: data }">
        <span class="font-semibold">{{ data?._index }}</span>
      </template>
      <template #action_type="{ row: data }">
        <div class="flex-y-center min-w-[100px] md:min-w-[auto]">
          <p
            v-if="data?.ft_order_side === 'sell'"
            class="flex-y-center gap-1 text-sm uppercase font-medium text-red"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.9987 14.6666C11.6806 14.6666 14.6654 11.6818 14.6654 7.99992C14.6654 4.31802 11.6806 1.33325 7.9987 1.33325C4.3168 1.33325 1.33203 4.31802 1.33203 7.99992C1.33203 11.6818 4.3168 14.6666 7.9987 14.6666Z"
                stroke="#E62E30"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.64844 8.84008L8.00177 6.49341L10.3551 8.84008"
                stroke="#E62E30"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            {{ $t("sell") }}
          </p>
          <p
            v-else
            class="flex-y-center gap-1 text-sm uppercase font-medium text-green"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.9987 14.6667C11.6806 14.6667 14.6654 11.6819 14.6654 8.00004C14.6654 4.31814 11.6806 1.33337 7.9987 1.33337C4.3168 1.33337 1.33203 4.31814 1.33203 8.00004C1.33203 11.6819 4.3168 14.6667 7.9987 14.6667Z"
                stroke="#16CC53"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.64844 7.16003L8.00177 9.5067L10.3551 7.16003"
                stroke="#16CC53"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            {{ $t("buy") }}
          </p>
        </div>
      </template>
      <template #transaction_number="{ row: data }">
        <div class="flex-y-center gap-1">
          <p class="text-sm leading-130 font-semibold text-dark-100">
            {{ data?.order_id }}
          </p>
          <CCopy :text="data?.order_id" />
        </div>
      </template>
      <template #order_date="{ row: data }">
        <span class="flex-y-center min-w-[100px] md:min-w-[auto]">
          {{
            data?.order_date ? convertToUserTimezone(data?.order_date) : "---"
          }}
        </span>
      </template>
      <template #quantity="{ row: data }">
        <span class="flex-y-center min-w-[100px] md:min-w-[auto]">
          {{ formatMoneyDecimal(data?.amount ?? 0, 2) }}
        </span>
      </template>
      <template #order_type="{ row: data }">
        <span class="flex-y-center min-w-[100px] md:min-w-[auto]">
          {{ data?.order_type }}
        </span>
      </template>
      <template #rate="{ row: data }">
        <span class="flex-y-center min-w-[100px] md:min-w-[auto]">
          {{ formatMoneyDecimal(data?.safe_price ?? 0, 4) }}
        </span>
      </template>
      <template #close_reason="{ row: data }">
        <span class="flex-y-center min-w-[100px] md:min-w-[auto]">
          {{ $t(checkForNull(data?.ft_order_tag)) }}
        </span>
      </template>
    </CTableWrapper>
  </CCard>
</template>

<script setup lang="ts">
import CCopy from "@/components/Common/CCopy.vue";
import { useMounted } from "@/composables/useMounted";
import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import CBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import CCard from "@/components/Card/CardWrapper.vue";
import { formatMoneyDecimal } from "@/utils";
import CProfileDetail from "@/components/Profile/CProfileDetail.vue";
import { useRoute } from "vue-router";
import ApiService from "@/services/ApiService";
import CCoinProfit from "@/modules/Bots/components/CCoinProfit.vue";
import CTableWrapper from "@/components/Common/Table/CTableWrapper.vue";
import { useTableFetch } from "@/composables/useTableFetch";
import CButton from "@/components/Common/CButton.vue";
import { useCommonStore } from "@/modules/Pages/stores";
import CCoinStatisticsShimmer from "@/components/Shimmer/CCoinStatisticsShimmer.vue";
import { useConvertToUserTimezone } from "@/composables/useConvertToUserTimezone";

const { convertToUserTimezone } = useConvertToUserTimezone();
const { mounted } = useMounted();
const { t, locale } = useI18n();
const route = useRoute();
const commonStore = useCommonStore();

const coinSingle = ref();
const single = ref();
const singleLoading = ref(true);

const coinID = computed(() => {
  return coinSingle.value?.base_currency?.title === "Avalanche"
    ? "avalanche-2"
    : coinSingle.value?.base_currency?.title === "Polygon"
      ? "matic-network"
      : coinSingle.value?.base_currency?.title === "Render"
        ? "render-token"
        : coinSingle.value?.base_currency?.title === "Internet Computer"
          ? "internet-computer"
          : coinSingle.value?.base_currency?.title === "Open Campus"
            ? "edu-coin"
            : coinSingle.value?.base_currency?.title === "Fetch.ai"
              ? "fetch-ai"
              : coinSingle.value?.base_currency?.title;
});

const trigger = computed(() => commonStore.trigger);

function getSingle() {
  ApiService.get(`/bot/${route.params.botId}`)
    .then((res) => {
      single.value = res?.data;
    })
    .finally(() => {
      singleLoading.value = false;
    });
}
const { tableData, loading } = useTableFetch(
  `/bot/${route.params.botId}/trades/${route.params.coinId}/orders/`,
);
const getCoinSingle = () => {
  ApiService.get(
    `/bot/${route.params.botId}/trades/${route.params.coinId}`,
  ).then((res) => {
    coinSingle.value = res?.data;
  });
};

getSingle();
getCoinSingle();

watch(
  () => route.params.coinId,
  () => {
    if (route.name === "BotsCoinSingle") {
      getSingle();
      getCoinSingle();
    }
  },
  { deep: true, immediate: true },
);

const openCoinList = computed(() => [
  {
    value: [coinSingle.value?.profit_abs, coinSingle.value?.profit_pct],
    type: "component",
    title: t("bot_single.current_profit"),
  },
]);
const closeCoinList = computed(() => [
  {
    value: [
      coinSingle.value?.close_profit_abs,
      coinSingle.value?.close_profit_pct,
    ],
    type: "component",
    title: t("bot_single.current_profit"),
  },
]);
const coinDetails = computed(() => [
  {
    value: convertToUserTimezone(coinSingle.value?.open_date),
    title: t("bot_single.open_date"),
  },
  {
    value: formatMoneyDecimal(coinSingle.value?.stake_amount, 2),
    title: t("bot_single.stake_amount"),
  },
  {
    value: formatMoneyDecimal(coinSingle.value?.amount, 4),
    title: t("bot_single.amount"),
  },
  {
    value: formatMoneyDecimal(coinSingle.value?.open_rate, 2),
    title: t("bot_single.open_rate"),
  },
  {
    value: formatMoneyDecimal(coinSingle.value?.current_rate, 2),
    title: t("bot_single.current_rate"),
  },
]);

const coinActiveDetail = computed(() =>
  coinSingle.value?.is_open
    ? [...coinDetails.value, ...openCoinList.value]
    : [...coinDetails.value, ...closeCoinList.value],
);

const checkForNull = (data: string) => {
  if (data === null || !data?.length) {
    return "-";
  } else {
    return data;
  }
};

const activeHeadCoins = [
  {
    title: "№",
    key: "_index",
  },
  {
    key: "transaction_number",
    title: "transaction_number",
  },
  {
    key: "action_type",
    title: "action_type",
  },
  {
    key: "order_date",
    title: "order_date",
  },

  {
    key: "rate",
    title: "rate",
  },
  {
    key: "quantity",
    title: "quantity",
  },
  {
    key: "order_type",
    title: "order_type",
  },
];
const noactiveHeadCoins = [
  {
    title: "№",
    key: "_index",
  },
  {
    key: "transaction_number",
    title: "transaction_number",
  },
  {
    key: "action_type",
    title: "action_type",
  },
  {
    key: "order_date",
    title: "order_date",
  },
  {
    key: "rate",
    title: "rate",
  },
  {
    key: "order_type",
    title: "order_type",
  },
  {
    key: "quantity",
    title: "quantity",
  },
  {
    key: "close_reason",
    title: "bot_single.close_reason",
  },
];

const routes = computed(() => [
  {
    name: coinSingle.value?.base_currency?.title,
    route: "",
  },
]);
</script>
<style>
.coin-image svg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
